import React, { useContext } from 'react';
import PropTypes from 'prop-types';
// import Lottie from 'react-lottie';
import moment from 'moment';

import {
  Paper,
  Popup,
  Space,
  Text,
  Conditional,
  Progress,
} from '@cashfree-intl/coherent';
import {
  roundOffNumber,
  numberWithCommas,
  convertAmountToAbbreviation,
  formatLargeNumber,
} from 'utils/common';
import { CURRENCY_SYMBOL } from 'utils/constants';

import { MerchantContext } from 'contexts/MerchantContext';
import CustomIcon from 'components/CustomIcons';
import InlineLoader from '../../../components/InlineLoader';
import {
  ALL_DAYS,
  WORKING_DAYS_ONLY,
  AVAILABLE_ON_MAPPING,
  SAME_DAY_SETTLEMENT,
} from '../constants';
import { formatInstantSettlementsDailyLimit } from '../utils';
import { StyledImportUnsettledAmountPaper } from '../styled';
// import * as animationData from 'animation/flash.json';

export const getRenewsInText = currTime => {
  const hours = 23 - currTime.hours();
  const mins = 60 - currTime.minute();

  if (mins === 60) {
    return {
      hours: hours + 1,
      mins: 0,
    };
  }
  return { hours, mins };
};

export const renderInfoContent = config => {
  const workingDayLimit = formatInstantSettlementsDailyLimit(
    config.workingDayLimitInLakhs,
  );

  const holidayLimit = formatInstantSettlementsDailyLimit(
    config.holidayLimitInLakhs,
  );

  const isWorkingDaysOnly = config.configType === WORKING_DAYS_ONLY;
  const isAllDays = config.configType === ALL_DAYS;

  return (
    <div className="info-content">
      <Space
        alignItems="center"
        className={`config-list-item ${isAllDays ? 'm-0' : ''}`}
      >
        <Text variant="b12" className="font-normal" color="bodyLight">
          Available on &nbsp;
        </Text>
        <Text variant="b12" className="font-normal">
          “{AVAILABLE_ON_MAPPING[config.configType]}”
        </Text>
      </Space>
      <Conditional if={isWorkingDaysOnly}>
        <Space
          className="config-list-item"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="b12" className="font-normal" color="bodyLight">
            Working Day Charges
          </Text>
          <Text variant="b12" className="font-normal" color="bodyLight">
            {roundOffNumber(config.workingDayRate)}% + GST
          </Text>
        </Space>
      </Conditional>
      <Conditional if={isAllDays}>
        <div className="info-divider my-1" />
        <Text variant="b12" className="config-type" color="placeholder">
          On Working Days
        </Text>
        <Space
          className="config-list-item"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="b12" className="font-normal" color="bodyLight">
            Limit
          </Text>
          <Text variant="b12" className="font-normal" color="bodyLight">
            {CURRENCY_SYMBOL.INR} {numberWithCommas(workingDayLimit.limit)}{' '}
            {workingDayLimit.suffix}
          </Text>
        </Space>
        <Space
          className="config-list-item"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="b12" className="font-normal" color="bodyLight">
            Charges
          </Text>
          <Text variant="b12" className="font-normal" color="bodyLight">
            {roundOffNumber(config.workingDayRate)}% + GST
          </Text>
        </Space>
        {/* Holidays */}
        <div className="info-divider my-1" />
        <Text variant="b12" className="config-type" color="placeholder">
          On Holidays
        </Text>
        <Space
          className="config-list-item"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="b12" className="font-normal" color="bodyLight">
            Limit
          </Text>
          <Text variant="b12" className="font-normal" color="bodyLight">
            {CURRENCY_SYMBOL.INR} {numberWithCommas(holidayLimit.limit)}{' '}
            {holidayLimit.suffix}
          </Text>
        </Space>
        <Space
          className="config-list-item"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="b12" className="font-normal" color="bodyLight">
            Charges
          </Text>
          <Text variant="b12" className="font-normal" color="bodyLight">
            {roundOffNumber(config.holidayRate)}% + GST
          </Text>
        </Space>
      </Conditional>
      <div className="info-divider my-1" />
      <Conditional if={config.isCycle !== SAME_DAY_SETTLEMENT}>
        <Text variant="b12" color="bodyLight" className="font-normal">
          For any queries, contact us at{' '}
          <a href="mailto:care@cashfree.com">care@cashfree.com.</a>
        </Text>
      </Conditional>
      <Conditional if={config.isCycle === SAME_DAY_SETTLEMENT}>
        <ul className="list-style-disc mt-0 mb-0 ml-2">
          <li>
            <Text variant="b12" color="bodyLight" className="font-normal">
              Same Day Settlements are initiated on working days at 9am & 5pm.
            </Text>
          </li>
          <li>
            <Text variant="b12" color="bodyLight" className="font-normal">
              For any queries, contact us at{' '}
              <a href="mailto:care@cashfree.com">care@cashfree.com.</a>
            </Text>
          </li>
        </ul>
      </Conditional>
    </div>
  );
};

const UnsettledAmountCard = ({
  unSettledAmountLoader,
  unSettledAmountData,
  config,
  isDailyLimitLoading,
}) => {
  // const defaultOptions = {
  //   loop: false,
  //   animationData,
  //   rendererSettings: {
  //     preserveAspectRatio: 'xMidYMid slice',
  //   },
  // };

  const { todayLimit = 0, remainingLimit = 0 } = config;

  const percent =
    100 - (todayLimit ? ((todayLimit - remainingLimit) / todayLimit) * 100 : 0);

  const { hours, mins } = getRenewsInText(moment());

  const { isMerchantImportEnabled } = useContext(MerchantContext);

  return (
    <>
      <Conditional if={!isMerchantImportEnabled}>
        <Paper className="unsettled-amount-card mb-3">
          <Space justifyContent="space-between">
            <div>
              <Text variant="h16" color="bodyLight">
                Unsettled Amount
              </Text>
              <InlineLoader
                loading={unSettledAmountLoader}
                content={
                  <Text variant="h28" className="mb-1 unsettled-amount">
                    {CURRENCY_SYMBOL.INR}{' '}
                    {numberWithCommas(
                      roundOffNumber(unSettledAmountData.unSettledAmount || 0),
                    ) || 0}
                  </Text>
                }
              />
              <Space alignItems="center" gap={1}>
                <Text>Settlement cycle - </Text>
                <InlineLoader
                  loading={isDailyLimitLoading}
                  content={
                    <Space alignItems="center" gap={1}>
                      <Conditional if={!isDailyLimitLoading}>
                        <Text strong>
                          {config.isActive ? config.isCycle : config.nsCycle}
                        </Text>
                      </Conditional>
                      <Conditional if={config.isActive && config.configType}>
                        <Popup
                          hoverable
                          className="settlements-cycle-info-popup p-2"
                          content={renderInfoContent(config)}
                          trigger={
                            <span className="flex pointer">
                              <CustomIcon componentName="INFO" />
                            </span>
                          }
                          position="top"
                        />
                      </Conditional>
                    </Space>
                  }
                />
              </Space>
            </div>
            <div>
              <Conditional if={!config.isActive}>
                <div className="standard-settlement-label">
                  <Text className="p-1" strong>
                    Standard Settlement
                  </Text>
                </div>
              </Conditional>
              <Conditional if={config.isActive}>
                <Space direction="column" alignItems="end">
                  <div className="instant-settlement-label p-1">
                    <Space alignItems="center">
                      {/* <Lottie options={defaultOptions} height={20} width={20} /> */}
                      <CustomIcon componentName="FLASH" />
                      <Text color="warning" strong className="ml-1">
                        Instant Settlement
                      </Text>
                    </Space>
                  </div>
                  <div>
                    <Space alignItems="center" className="my-1">
                      <Text
                        variant="b12"
                        className={`w-400 font-normal ${
                          isDailyLimitLoading ? 'mr-1' : ''
                        }`}
                        color="bodyLight"
                      >
                        Daily Limit:{' '}
                      </Text>
                      <InlineLoader
                        loading={isDailyLimitLoading}
                        content={
                          <>
                            <Text
                              variant="h16"
                              color="warning"
                              strong
                              className="ml-1 font-normal instant-settlements-remaining-limit"
                            >
                              {convertAmountToAbbreviation(
                                remainingLimit,
                                true,
                              )}
                            </Text>
                            <Text variant="b14" className="w-400 font-normal">
                              left out of{' '}
                              {convertAmountToAbbreviation(todayLimit, true)}
                            </Text>
                          </>
                        }
                      />
                    </Space>
                    <Conditional if={!isDailyLimitLoading}>
                      <Progress
                        className={`cf-progress-bar orange-bar m-0 ${
                          percent <= 0 ? 'empty-progress-bar' : ''
                        } ${percent === 100 ? 'full-progress-bar' : ''}`}
                        percent={percent}
                        size="xs"
                      />
                      <Text
                        variant="b12"
                        className="w-400 font-normal mt-1 text-right"
                        color="bodyLight"
                      >
                        Renewing in{' '}
                        {`${hours ? `${hours} hr` : ''} ${
                          mins ? `${mins} mins` : ''
                        }`}
                      </Text>
                    </Conditional>
                  </div>
                </Space>
              </Conditional>
            </div>
          </Space>
        </Paper>
      </Conditional>
      <Conditional if={isMerchantImportEnabled}>
        <StyledImportUnsettledAmountPaper className="mb-3">
          <Space direction="column" gap={2}>
            <Text variant="h16" color="bodyLight">
              Unsettled Amount
            </Text>
            <InlineLoader
              loading={unSettledAmountLoader}
              content={
                <Text variant="h28" className="mb-1 unsettled-amount">
                  {CURRENCY_SYMBOL.INR}{' '}
                  {formatLargeNumber(unSettledAmountData?.unSettledAmount)}
                </Text>
              }
            />
          </Space>
        </StyledImportUnsettledAmountPaper>
      </Conditional>
    </>
  );
};

UnsettledAmountCard.propTypes = {
  unSettledAmountLoader: PropTypes.bool.isRequired,
  unSettledAmountData: PropTypes.object.isRequired,
  isDailyLimitLoading: PropTypes.bool.isRequired,
  config: PropTypes.object.isRequired,
};

export default UnsettledAmountCard;
